'use client';

import type { CSSProperties } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { dur, ease, sceneItem, spring, springFade, stagger } from '@/lib/motion';

/**
 * Marka şeridi — "ölçüm paneli".
 *
 * Eski hâli wask.co'nun düz logo sırasıydı: ortalanmış gri bir başlık, altında
 * yan yana dizilmiş logolar. İki sorunu vardı:
 *
 *   1. Logolar eşit görünmüyordu. Hepsi `height: 28px` ile hizalanıyordu ama
 *      en/boy oranları 1.0 ile 6.0 arasında değişiyor — aynı yükseklikte
 *      Webflow 167px, Coinbase 28px yer kaplıyordu. Göz yüksekliği değil
 *      ALANI ölçüyor; sıra dengesiz duruyordu.
 *   2. Hiç hareketi yoktu ve sayfanın geri kalanındaki 1px'lik ızgara diline
 *      bağlanmıyordu.
 *
 * Yeni yapı, sayfadaki mevcut hairline dilini bir adım ileri taşıyor: logolar
 * eşit hücrelere oturuyor, hücreleri 1px'lik çizgiler ayırıyor, çerçevenin
 * köşelerinde ölçü işaretleri var.
 *
 * Şerit eskiden görüş alanına girdiğinde kuruluyordu (çizgiler → logolar);
 * o giriş 9 Eyl 2026'da kullanıcı kararıyla kaldırıldı, şerit sayfa açılır
 * açılmaz kurulu hâlde duruyor.
 *
 * ── Logoları eşitleyen üç karar ───────────────────────────────────────────
 *
 * • **Ortak kutu.** Her logo aynı ölçüdeki 5:1 kutuya `contain` ile oturur.
 *   Yükseklik değil kutu sabit; oranı ne olursa olsun hiçbiri kutuyu aşamaz.
 * • **Tek renk.** Logolar `mask-image` ile tek bir açık tonda basılıyor —
 *   kaynak SVG'nin kendi renkleri (Shopify yeşili, Webflow mavisi, Asana
 *   mercanı) taban durumda devrede değil. Renk de bir ağırlıktır; renkli olan
 *   öne fırlıyordu. Gerçek renk yalnızca imleç hücrenin üstündeyken açılır.
 * • **Optik pay.** Kutu eşitliği matematiksel; göz hâlâ ufak farklar görüyor
 *   (NVIDIA kalın kapitalleriyle ağır, Shopify oranı gereği küçük kalıyor).
 *   `scale` alanı bunun ayar vidası — ±%10 içinde kalmalı, fazlası kutu
 *   eşitliğini bozar.
 *
 * Kaynak SVG'ler svgl.app'ten (koyu zemin varyantları). viewBox'ları mürekkebe
 * göre kırpıldı: svgl'de logolar kendi kanvaslarının ortasında duruyor ve
 * Coinbase 24x24'lük kutunun içinde yalnızca 4.3 birimlik bir şeritti —
 * `contain` ile hizalanınca diğerlerinin yarısı kadar kalıyordu.
 */

/**
 * `scale`: kutu eşitliğinden sonra kalan optik farkın payı.
 *
 * Değerler tahminle değil ölçülerek seçildi: altı logo ortak kutuya sığdırılıp
 * maskelenmiş hâlleriyle rasterlendi, basılan piksel alanları karşılaştırıldı.
 * Şu an 2100–2700 px² bandında (ortalamanın ±%12'si); ham kutu eşitliğinde
 * Shopify 1900, Webflow 2900 ile bandın iki ucundaydı — biri soluk, diğeri
 * kalın duruyordu.
 */
const BRANDS = [
  { slug: 'shopify', name: 'Shopify', scale: 1.06 },
  { slug: 'webflow', name: 'Webflow', scale: 1 },
  { slug: 'dropbox', name: 'Dropbox', scale: 1 },
  { slug: 'coinbase', name: 'Coinbase', scale: 1.04 },
  { slug: 'asana', name: 'Asana', scale: 1 },
  { slug: 'nvidia', name: 'NVIDIA', scale: 0.96 },
] as const;

/**
 * Hücreleri ayıran 1px'lik çizgiler. Sütun sayısı kırılma noktasına göre
 * 2 → 3 → 6 diye değiştiği için çizgiler hücrelere değil ızgaranın kendisine
 * yüzdeyle konumlanıyor; hangi genişlikte hangisinin görüneceğine CSS karar
 * veriyor (globals.css → "Marka şeridi").
 *
 * Yüzdeler kesişiyor: 6 sütunun 2/6'sı ile 3 sütunun 1/3'ü aynı yer. Bu yüzden
 * sekiz çizgi üç yerleşimin tamamına yetiyor.
 */
const LINES = [
  { key: 'v17', axis: 'v', at: 100 / 6 },
  { key: 'v33', axis: 'v', at: 200 / 6 },
  { key: 'v50', axis: 'v', at: 50 },
  { key: 'v66', axis: 'v', at: 400 / 6 },
  { key: 'v83', axis: 'v', at: 500 / 6 },
  { key: 'h33', axis: 'h', at: 100 / 3 },
  { key: 'h50', axis: 'h', at: 50 },
  { key: 'h66', axis: 'h', at: 200 / 3 },
] as const;

/* Sıra: önce başlık, sonra ızgara. Şerit kendini "kuruyor". */
const frameV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const headV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/** Yatay çizgi X'te, dikey Y'de büyür — ikisi de merkezinden. Tek varyant
 *  ikisine de yetiyor: 1px'lik kenarda diğer eksenin ölçeklenmesi görünmüyor. */
const gridV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight, delayChildren: 0.04 } },
};

const lineV: Variants = {
  hidden: { opacity: 0, scaleX: 0, scaleY: 0 },
  show: { opacity: 1, scaleX: 1, scaleY: 1, transition: springFade(spring.default) },
};

const tickV: Variants = {
  hidden: { opacity: 0, scale: 0.4 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.snap) },
};

export function Brands() {
  const t = useTranslations('brands');
  const reduceMotion = useReducedMotion();

  return (
    <section className="brands-rail" aria-labelledby="brands-rail-title">
      {/* Yan sütunlar basılmıyor: taşıdıkları çapraz tarama sayfadan tamamen
          kaldırıldı (kullanıcı kararı — globals.css → "Çapraz tarama
          kaldırıldı"). Şeridin görünen kenarı .brands-rail-frame'in dikey
          hatları; .grid-item-8fr `grid-column: 2` ile kendi yerleşiyor. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div
            className="brands-rail-frame"
            variants={frameV}
            initial={false}
            animate="show"
          >
            <motion.div className="brands-rail-head" variants={headV}>
              {/*
                Canlı işareti. Sonsuz döngü olduğu için MotionConfig'in
                reducedMotion="user" ayarı yetmiyor (DESIGN.md §1) — tercih
                açıkken nokta sabit yanar, nabız durur.

                🔑 Opaklık SIFIRDAN başlayıp sıfırda bitiyor. Önceki hâli
                [0.5 → 0] idi: döngü başa sardığında opaklık 0'dan 0.5'e tek
                karede sıçrıyor, halka da scale 1'e — yani noktanın tam
                üstündeki 8px'lik çembere — geri dönüyordu. Görünen şey her
                2.4 saniyede bir çakan tam yuvarlak bir parlamaydı.

                Artık iki uç da görünmez, dolayısıyla sarma anı görünmüyor.
                `times` opaklığın tepe noktasını %16'ya çekiyor: halka hızlı
                beliriyor, kalan sürede genişleyerek sönüyor — radar darbesi.
              */}
              <span className="brands-rail-dot" aria-hidden="true">
                {!reduceMotion && (
                  <motion.span
                    className="brands-rail-dot-ring"
                    animate={{ scale: [1, 1.3, 2.4], opacity: [0, 0.5, 0] }}
                    transition={{
                      duration: 2.4,
                      repeat: Infinity,
                      ease: 'easeOut',
                      times: [0, 0.16, 1],
                    }}
                  />
                )}
              </span>

              <h2 className="brands-rail-title" id="brands-rail-title">
                {t('title')}
              </h2>

              <motion.span
                className="brands-rail-rule"
                aria-hidden="true"
                variants={{
                  hidden: { scaleX: 0 },
                  show: { scaleX: 1, transition: { duration: dur.slow, ease: ease.out } },
                }}
              />
            </motion.div>

            <motion.div className="brands-rail-grid" variants={gridV}>
              {LINES.map(({ key, axis, at }) => (
                <motion.span
                  key={key}
                  className={`brands-rail-line brands-rail-line--${axis} brands-rail-line--${key}`}
                  /* Dikey çizgiler satırın BAŞINDAN ölçülü — ızgara sağdan
                     sola dilde aynalanınca çizgiler de karolarla birlikte. */
                  style={{ [axis === 'v' ? 'insetInlineStart' : 'top']: `${at}%` }}
                  variants={lineV}
                  aria-hidden="true"
                />
              ))}

              {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => (
                <motion.span
                  key={corner}
                  className={`brands-rail-tick brands-rail-tick--${corner}`}
                  variants={tickV}
                  aria-hidden="true"
                />
              ))}

              {BRANDS.map(({ slug, name, scale }) => (
                <motion.div key={slug} className="brands-rail-cell" variants={sceneItem}>
                  <span
                    className="brands-rail-logo"
                    style={
                      {
                        '--logo-src': `url("/logos/brands/${slug}.svg")`,
                        '--logo-scale': scale,
                      } as CSSProperties
                    }
                  >
                    <span className="brands-rail-logo-mono" aria-hidden="true" />
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      className="brands-rail-logo-color"
                      src={`/logos/brands/${slug}.svg`}
                      alt={name}
                      loading="lazy"
                      decoding="async"
                    />
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
